import React, { useEffect, useState, createContext, useContext } from 'react';
import { ResolvedRestaurant, SupabaseProduct } from '../types/restaurant';
import { normalizeDomain, resolveRestaurant, getProductsByRestaurant } from '../lib/restaurant';
interface RestaurantContextType {
  /** The normalized hostname used for domain resolution */
  cleanHost: string;
  /** The resolved restaurant profile — null until loaded */
  restaurant: ResolvedRestaurant | null;
  /** Shortcut to restaurant.restaurant_id */
  restaurantId: string;
  /** Active products for this restaurant */
  products: SupabaseProduct[];
  /** Unique categories derived from products */
  categories: string[];
  /** Whether the initial resolution is still loading */
  loading: boolean;
  /** Error message if resolution failed */
  error: string | null;
  /** Re-fetch products (e.g. after admin changes) */
  refreshProducts: () => Promise<void>;
}
const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);
export function RestaurantProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [cleanHost] = useState(() => normalizeDomain(window.location.hostname));
  const [restaurant, setRestaurant] = useState<ResolvedRestaurant | null>(null);
  const [products, setProducts] = useState<SupabaseProduct[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Resolve restaurant on mount
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const resolved = await resolveRestaurant(cleanHost);
        if (cancelled) return;
        if (!resolved) {
          setError('Restaurant not configured for this domain');
          setLoading(false);
          return;
        }
        setRestaurant(resolved);
        // Fetch products for this restaurant
        const prods = await getProductsByRestaurant(resolved.restaurant_id);
        if (cancelled) return;
        setProducts(prods);
        // Derive unique categories preserving sort order
        const seen = new Set<string>();
        const cats: string[] = [];
        prods.forEach(p => {
          if (!seen.has(p.category)) {
            seen.add(p.category);
            cats.push(p.category);
          }
        });
        setCategories(cats);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Restaurant resolution failed:', err);
        setError('Failed to load restaurant configuration');
        setLoading(false);
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [cleanHost]);
  const refreshProducts = async () => {
    if (!restaurant) return;
    const prods = await getProductsByRestaurant(restaurant.restaurant_id);
    setProducts(prods);
    const seen = new Set<string>();
    const cats: string[] = [];
    prods.forEach(p => {
      if (!seen.has(p.category)) {
        seen.add(p.category);
        cats.push(p.category);
      }
    });
    setCategories(cats);
  };
  const restaurantId = restaurant?.restaurant_id || '';
  return <RestaurantContext.Provider value={{
    cleanHost,
    restaurant,
    restaurantId,
    products,
    categories,
    loading,
    error,
    refreshProducts
  }} data-id="element-1298">
      {children}
    </RestaurantContext.Provider>;
}
export function useRestaurant() {
  const context = useContext(RestaurantContext);
  if (context === undefined) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
}